import { useEffect, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Core } from "@pdftron/webviewer/types";

import Button from "components/ui/button/Button";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { createUseDebounce } from "lib/debounce";
import UIcon from "lib/icons/untitled_ui/UIcon";
import FileViewer, { useFileViewerInstance } from "../../lib/core_components/FileViewer";
import { Flex, Text } from "../../styles/utility-components";
import { ScorecardSource } from "./types";

import css from "./DocumentViewer.module.scss";

type Props = {
  documentUrl: string;
  fileExtension?: string;
  source: ScorecardSource;
  open: boolean;
};

export default function DocumentViewer({ documentUrl, fileExtension, source, open }: Props) {
  const { instance } = useFileViewerInstance();
  const [documentReady, setIsDocumentReady] = useState(false);

  const [annotationCache, setAnnotationCache] = useState<Core.Annotations.RectangleAnnotation[]>(
    [],
  );
  const [currentAnnotationIdx, setCurrentAnnotationIdx] = useState(0);
  const navigateByControls = useRef(false);

  const addAnnotationToCache = (annotation: Core.Annotations.RectangleAnnotation) => {
    setAnnotationCache((prevAnnotations) =>
      [...prevAnnotations, annotation].sort((a, b) => a.PageNumber - b.PageNumber),
    );
  };

  useEffect(() => {
    setIsDocumentReady(false);
  }, [documentUrl]);

  useEffect(() => {
    const documentViewer = instance?.Core.documentViewer;
    if (!documentViewer) {
      return;
    }

    const onDocumentReady = () => {
      setIsDocumentReady(true);
    };

    // Waiting for documentLoaded event is not sufficient and gives incomplete results
    // Instead, wait for the last event of the document lifecycle
    documentViewer.addEventListener("annotationsLoaded", onDocumentReady);

    // Clean up event listeners
    return () => {
      documentViewer.removeEventListener("annotationsLoaded", onDocumentReady);
    };
  }, [instance]);

  useEffect(() => {
    if (!instance || !documentReady) {
      return;
    }

    if (!open) {
      return;
    }

    const { documentViewer, annotationManager, Annotations } = instance.Core;
    annotationManager.deleteAnnotations(annotationManager.getAnnotationsList(), {
      force: true,
    });
    setAnnotationCache([]);

    if (!source) {
      documentViewer.setCurrentPage(1, false);
      return;
    }

    for (let lIdx = 0; lIdx < source.locations.length; ++lIdx) {
      const location = source.locations[lIdx];
      const pageNumber = location.pageNumber;
      const bbox = location.bbox;
      if (bbox) {
        const rectAnnotation = new Annotations.RectangleAnnotation({
          PageNumber: pageNumber,
          X: bbox.x - 10,
          Y: bbox.y - 5,
          Width: bbox.width + 20,
          Height: bbox.height + 10,
          StrokeColor: new Annotations.Color(0, 96, 255, 0.8),
          StrokeThickness: 2,
        });
        rectAnnotation.ReadOnly = true;
        if (lIdx === 0) {
          // Jump to the first annotation after it's been drawn
          const jumper = () => {
            annotationManager.jumpToAnnotation(rectAnnotation);
            setCurrentAnnotationIdx(0);
            annotationManager.removeEventListener("annotationsDrawn", jumper);
          };
          annotationManager.addEventListener("annotationsDrawn", jumper);
        }
        annotationManager.addAnnotation(rectAnnotation);
        addAnnotationToCache(rectAnnotation);
        annotationManager.redrawAnnotation(rectAnnotation);
      } else {
        if (lIdx === 0) {
          // jump to page if there is no bbox
          documentViewer.setCurrentPage(pageNumber, false);
        }
      }
    }
  }, [open, documentReady, instance, source]);

  const useDebounce = createUseDebounce(500);
  const debouncedOnPageChanged = useDebounce((currentPage: number) => {
    if (navigateByControls.current) {
      navigateByControls.current = false;
      return;
    }
    const annotationIdx = annotationCache.findIndex(({ PageNumber }) => PageNumber === currentPage);
    if (annotationIdx !== -1) {
      setCurrentAnnotationIdx(annotationIdx);
    }
  });

  useEffect(() => {
    if (!instance) return;

    const { documentViewer } = instance.Core;
    documentViewer.addEventListener("pageNumberUpdated", debouncedOnPageChanged);

    return () => {
      documentViewer.removeEventListener("pageNumberUpdated", debouncedOnPageChanged);
    };
  }, [instance, debouncedOnPageChanged]);

  const handleNextClick = () => {
    if (!instance || !annotationCache.length) return;

    const { documentViewer } = instance.Core;
    const currentPage = documentViewer.getCurrentPage();
    // Find the next annotation on the current page or after it
    const nextAnnotationIdx = annotationCache.findIndex(
      (annotation, idx) =>
        annotation.PageNumber > currentPage ||
        (annotation.PageNumber === currentPage && idx > currentAnnotationIdx),
    );

    if (nextAnnotationIdx !== -1) {
      documentViewer.setCurrentPage(annotationCache[nextAnnotationIdx].PageNumber, true);
      setCurrentAnnotationIdx(nextAnnotationIdx);
    }
    // Cycle to the first annotation if there is no next one
    else {
      documentViewer.setCurrentPage(annotationCache[0].PageNumber, true);
      setCurrentAnnotationIdx(0);
    }
    navigateByControls.current = true;
  };

  const handlePrevClick = () => {
    if (!instance) return;

    const { documentViewer } = instance.Core;
    const currentPage = documentViewer.getCurrentPage();
    // Find the last annotation on the current page or before it
    const prevAnnotationIdx = annotationCache.findLastIndex(
      (annotation, idx) =>
        annotation.PageNumber < currentPage ||
        (annotation.PageNumber === currentPage && idx < currentAnnotationIdx),
    );

    if (prevAnnotationIdx !== -1) {
      documentViewer.setCurrentPage(annotationCache[prevAnnotationIdx].PageNumber, true);
      setCurrentAnnotationIdx(prevAnnotationIdx);
    }
    // Cycle to the last annotation if there is no previous one
    else {
      documentViewer.setCurrentPage(annotationCache[annotationCache.length - 1].PageNumber, true);
      setCurrentAnnotationIdx(annotationCache.length - 1);
    }
    navigateByControls.current = true;
  };

  const hasAnnotations = annotationCache.length > 0;

  return (
    <div className={css.container}>
      <div className={css.header}>
        {source.fileName && (
          <EllipsisTooltipText
            fullText={source.fileName}
            containerClassname={css.tooltipContainer}
          />
        )}
        {instance && (
          <ControlsContainer>
            {documentReady && (
              <Flex gap={8} alignItems="center">
                {hasAnnotations ? (
                  <Text>
                    Viewing {currentAnnotationIdx + 1} of {annotationCache.length} matches
                  </Text>
                ) : (
                  <Text>No matches found</Text>
                )}
                <Button buttonType="secondary" onClick={handlePrevClick} disabled={!hasAnnotations}>
                  <UIcon icon="chevronUp" />
                  Previous
                </Button>
                <Button buttonType="secondary" onClick={handleNextClick} disabled={!hasAnnotations}>
                  <UIcon icon="chevronDown" />
                  Next
                </Button>
              </Flex>
            )}
            <Button
              buttonType="secondary"
              className={css.searchBtn}
              onClick={() => {
                const isSearchPanelOpen = instance.UI.isElementOpen("searchPanel");

                if (isSearchPanelOpen) {
                  instance.UI.closeElements(["searchPanel"]);
                } else {
                  instance.UI.openElements(["searchPanel"]);
                }
              }}
            >
              <SearchOutlined />
            </Button>
          </ControlsContainer>
        )}
      </div>
      <FileViewer
        documentUrl={documentUrl}
        fileExtension={fileExtension}
        disabledElements={[
          "header",
          "toolsHeader",
          "textPopup",
          "contextMenuPopup",
          "printModal",
          "errorModal",
        ]}
      />
    </div>
  );
}

const ControlsContainer = styled(Flex)({
  gap: 16,
  flexShrink: 0,
  marginLeft: 16,
});
